// Mirrors the backend's Application/Billing/DTOs/BillingDTOs.cs exactly — not auto-derived, keep in
// lockstep by hand (same convention as types/auth.ts's PERMISSIONS catalog).

export type ChargeCategory = 'Storage' | 'Handling' | 'VAS';
export type RateBasis = 'PerPalletDay' | 'PerSqFtDay' | 'PerUnit' | 'PerTransaction' | 'Flat';
export type InvoiceStatus = 'Draft' | 'Finalized' | 'Sent' | 'Paid' | 'Cancelled';
export type GstType = 'Intra' | 'Inter';

// Fixed ChargeCode values the billing engine recognizes for automatic Storage/Handling computation
// — see the backend's Application/Billing/Services/BillingChargeCodes.cs. VAS charges are free text
// since they're never looked up from the rate card.
export const BILLING_CHARGE_CODES = {
  STORAGE_PER_DAY: 'STORAGE_PERDAY',
  INBOUND_PER_GRN: 'INBOUND_GRN',
  INBOUND_PER_UNIT: 'INBOUND_UNIT',
  OUTBOUND_PER_DISPATCH: 'OUTBOUND_DISPATCH',
  OUTBOUND_PER_UNIT: 'OUTBOUND_UNIT',
} as const;

export interface ClientRateCardLineDto {
  id: string;
  chargeCategory: ChargeCategory;
  chargeCode: string;
  description: string | null;
  basis: RateBasis;
  rate: number;
  unitOfMeasure: string | null;
  minChargeAmount: number | null;
  gstRatePercent: number;
  sacCode: string | null;
  status: string;
}

export interface ClientRateCardLineRequest {
  chargeCategory: ChargeCategory;
  chargeCode: string;
  description: string | null;
  basis: RateBasis;
  rate: number;
  unitOfMeasure: string | null;
  minChargeAmount: number | null;
  gstRatePercent: number;
  sacCode: string | null;
}

export interface ClientContractDto {
  id: string;
  clientId: string;
  clientCode: string;
  clientName: string;
  contractNumber: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  billingCycle: string;
  status: string;
  currency: string;
  rateLines: ClientRateCardLineDto[];
}

export interface CreateClientContractRequest {
  clientId: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  billingCycle: string;
  currency: string;
  rateLines: ClientRateCardLineRequest[];
}

export interface UpdateClientContractRequest {
  contractNumber: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  billingCycle: string;
  status: string;
  currency: string;
  rateLines: ClientRateCardLineRequest[];
}

export interface VasChargeDto {
  id: string;
  clientId: string;
  clientName: string;
  chargeDate: string;
  chargeCode: string;
  description: string | null;
  quantity: number;
  rate: number;
  gstRatePercent: number;
  amount: number;
  status: 'Unbilled' | 'Billed';
  invoiceLineId: string | null;
}

export interface CreateVasChargeRequest {
  clientId: string;
  chargeDate: string;
  chargeCode: string;
  description: string | null;
  quantity: number;
  rate: number;
  gstRatePercent: number;
}

export interface InvoiceLineDto {
  id: string;
  chargeCategory: ChargeCategory;
  chargeCode: string;
  description: string | null;
  quantity: number;
  rate: number;
  amount: number;
  gstRatePercent: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  referenceType: string | null;
  referenceId: string | null;
}

export interface InvoiceDto {
  id: string;
  clientId: string;
  clientCode: string;
  clientName: string;
  invoiceNumber: string;
  invoiceDate: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  supplierStateCode: string | null;
  recipientStateCode: string | null;
  gstType: GstType;
  subTotal: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  totalAmount: number;
  status: InvoiceStatus;
  pdfPath: string | null;
  notes: string | null;
  lines: InvoiceLineDto[];
}

export interface GenerateInvoiceRequest {
  clientId: string;
  periodStart: string;
  periodEnd: string;
  notes: string | null;
}
